import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'md2pdf.font-family';

const readStoredFont = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const FontPicker = ({ className }) => {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFont, setSelectedFont] = useState(readStoredFont);

  useEffect(() => {
    if (selectedFont) {
      document.documentElement.style.setProperty(
        '--markdown-font-family',
        JSON.stringify(selectedFont)
      );
    }
  }, [selectedFont]);

  const loadFonts = async () => {
    if (!window.queryLocalFonts) {
      setMessage('Local font access requires a supported Chrome or Edge browser.');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const localFonts = await window.queryLocalFonts();
      const families = Array.from(
        new Set(localFonts.map(font => font.family).filter(Boolean))
      ).sort((left, right) => left.localeCompare(right));

      setFonts(families);
      if (families.length === 0) {
        setMessage('No local fonts were returned by the browser.');
      }
    } catch (error) {
      setMessage(
        error && error.name === 'NotAllowedError'
          ? 'Local font access was denied.'
          : 'Unable to load local fonts.'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectFont = event => {
    const family = event.target.value;
    setSelectedFont(family);
    if (!family) {
      document.documentElement.style.removeProperty('--markdown-font-family');
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // The selected font still applies for the current session.
      }
      return;
    }

    document.documentElement.style.setProperty(
      '--markdown-font-family',
      JSON.stringify(family)
    );
    try {
      window.localStorage.setItem(STORAGE_KEY, family);
    } catch (error) {
      // The selected font still applies for the current session.
    }
  };

  const fontOptions = Array.from(new Set([...fonts, selectedFont].filter(Boolean)));

  return (
    <div className={className}>
      <button type="button" onClick={loadFonts} disabled={loading}>
        {loading ? 'Loading…' : 'Fonts'}
      </button>
      <select aria-label="PDF font" value={selectedFont} onChange={selectFont}>
        <option value="">System default</option>
        {fontOptions.map(font => (
          <option key={font} value={font}>
            {font}
          </option>
        ))}
      </select>
      {message && (
        <span className="font-message" title={message} aria-label={message}>
          !
        </span>
      )}
    </div>
  );
};

export default FontPicker;
